import type {
	BeckyCssModuleResponse,
	BeckyFunctionLibrary,
	BeckyRequestPayload,
	SiteAssetsModule,
	CssInstanceModel,
	BeckyModelCssModuleResponse,
} from '@wix/thunderbolt-becky-root'
import { createFunctionLibrary, deStringifyExperiments } from '@wix/thunderbolt-becky-root'
import byRefModule from '../../byRef'
import type { BeckyExperiments } from '@wix/thunderbolt-commons'
import type { BeckyTopology, FetchFn, MetricsReporter, ModuleFetcher, Logger } from '@wix/thunderbolt-becky-types'
import { maybeAppendErrorPages } from '../../errorPages/getPayloadWithErrorPage'
import { BeckyFetch } from '../utils/beckyFetch'
import { getBeckyTopology } from '../helpers/topologyBuilder'
import schemas from '@wix/document-services-json-schemas/dist/refData.json'
import { getShouldRunCarmi, shouldReturnResolvedBeckyModelFromPayload } from './cssModuleUtils'
import { tryOptimizeCss } from './optimizeCss'
import { getCarmiCss } from './carmiCss'
import { getCatharsisCss } from './catharsisCss'
import { getBeckyModel } from './cssBeckyModel'

const getCssResult = async ({
	payload,
	beckyTopology,
	funcLib,
	moduleFetcher,
	fetchFn,
	metricsReporter,
	logger,
	experiments,
}: {
	payload: BeckyRequestPayload
	beckyTopology: BeckyTopology
	funcLib: BeckyFunctionLibrary
	moduleFetcher: ModuleFetcher
	fetchFn: FetchFn
	metricsReporter: MetricsReporter
	logger: Logger
	experiments: BeckyExperiments
}): Promise<CssInstanceModel> => {
	// Replace setter with a noop function because fetchApi.carmi assumes that the setter exists
	const ccsFuncLib = { ...funcLib, setFetchRequestsStore: () => metricsReporter.reportError }

	const { beckyModel } = await getBeckyModel(
		payload,
		beckyTopology,
		ccsFuncLib,
		moduleFetcher,
		fetchFn,
		metricsReporter,
		logger,
		experiments
	)

	return funcLib.runAsyncAndReport(async () => {
		// @ts-ignore
		globalThis.isDynamicSlotExperimentOpen = !!beckyModel.experiments['specs.thunderbolt.dynamicSlots']
		// @ts-ignore
		globalThis.spxStopperExperiment = !!beckyModel.experiments['specs.thunderbolt.spxStopper']
		// @ts-ignore
		globalThis.shouldUseClassSelectors = !!(beckyModel.experiments['specs.thunderbolt.useClassSelectors'] || beckyModel.isBuilderComponentModel)
		// @ts-ignore
		globalThis.interactionsFeatureExperiment = !!(beckyModel.experiments['specs.thunderbolt.interactionsFeature'] || beckyModel.isBuilderComponentModel)

		const { catharsisCss, cssResultObject } = getCatharsisCss(payload, funcLib, beckyModel, logger)

		// when this experiment is true, then shouldRunCarmi shouldn't event be calculated (because we don't want carmi)
		const dontRunCarmiExperimentOpen = !!beckyModel.experiments['specs.thunderbolt.removeCarmiCssFromBecky']

		const shouldRunCarmi = getShouldRunCarmi(payload, beckyModel.featuresToRun, beckyModel.featuresToIgnore)
		if (dontRunCarmiExperimentOpen || !shouldRunCarmi) {
			return { css: catharsisCss, cssResultObject }
		}

		const carmiCss = await getCarmiCss(beckyModel, ccsFuncLib)
		// @ts-ignore
		delete globalThis.isDynamicSlotExperimentOpen
		// @ts-ignore
		delete globalThis.isSpxStopperExperimentOn
		// @ts-ignore
		delete globalThis.shouldUseClassSelectors
		// @ts-ignore
		delete globalThis.interactionsFeatureExperiment
		const css = carmiCss + catharsisCss
		return { css, cssResultObject }
	}, 'tb-css-model-calculation')
}

const returnResolvedBeckyModelFromPayload = async (
	payload: BeckyRequestPayload,
	beckyTopology: BeckyTopology,
	fetchFn: FetchFn,
	metricsReporter: MetricsReporter,
	funcLib: BeckyFunctionLibrary,
	moduleFetcher: ModuleFetcher,
	logger: Logger,
	experiments: BeckyExperiments
) => {
	const ccsFuncLib = { ...funcLib, setFetchRequestsStore: () => metricsReporter.reportError }

	const { beckyModel, partialStylableInstanceParams } = await getBeckyModel(
		payload,
		beckyTopology,
		ccsFuncLib,
		moduleFetcher,
		fetchFn,
		metricsReporter,
		logger,
		experiments
	)

	return {
		beckyModel: JSON.stringify(beckyModel),
		css: '',
		stylableInstanceParams: JSON.stringify(partialStylableInstanceParams),
	}
}

const getOptimizedCssResult = async (
	payload: BeckyRequestPayload,
	beckyTopology: BeckyTopology,
	experiments: BeckyExperiments,
	fetchFn: FetchFn,
	metricsReporter: MetricsReporter,
	funcLib: BeckyFunctionLibrary,
	moduleFetcher: ModuleFetcher,
	logger: Logger
) => {
	// @ts-ignore
	const { css, cssResultObject = {} } = await getCssResult({
		payload,
		beckyTopology,
		funcLib,
		moduleFetcher,
		fetchFn,
		metricsReporter,
		logger,
		experiments,
	})

	const optimizedCss = await tryOptimizeCss(css, payload, logger, metricsReporter)

	return { css: optimizedCss, cssResultObject }
}

const cssModule: SiteAssetsModule<BeckyCssModuleResponse | BeckyModelCssModuleResponse> = (moduleContext) => {
	const { httpClient, metricsReporter, topologyGetter, moduleFetcher, logger } = moduleContext
	const fetchFn = BeckyFetch(httpClient.fetch, metricsReporter)
	const funcLib = createFunctionLibrary({
		fetch: fetchFn,
		metricsReporter,
		schemas,
	})
	const byRef = byRefModule(moduleContext)

	return {
		execute: async (payload) => {
			const experiments = deStringifyExperiments(payload.beckyExperiments)
			const beckyTopology = getBeckyTopology(topologyGetter, payload)

			const payloadWithResolvedRefs = await byRef.execute(maybeAppendErrorPages(payload))

			if (shouldReturnResolvedBeckyModelFromPayload(payloadWithResolvedRefs)) {
				/* This is for debugging CSS locally.
				The way it works would be:
					1. css module returns serialized and resolved beckyModel (thus stylableInstance is not included)
					2. Client side, via PageStyleLoader, will dynamically import VSM and run it with resolved beckyModel
				 **/
				return returnResolvedBeckyModelFromPayload(
					payloadWithResolvedRefs,
					beckyTopology,
					fetchFn,
					metricsReporter,
					funcLib,
					moduleFetcher,
					logger,
					experiments
				)
			}

			return getOptimizedCssResult(
				payloadWithResolvedRefs,
				beckyTopology,
				experiments,
				fetchFn,
				metricsReporter,
				funcLib,
				moduleFetcher,
				logger
			)
		},
	}
}

export default cssModule
